import React, { useEffect, useState } from "react";
import Select from "react-select";
import ReactQuill, { Quill } from "react-quill";
import MaterialTable from "material-table";
import "react-quill/dist/quill.snow.css";

// reactstrap components
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  FormGroup,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Row,
  Col,
  Badge,
} from "reactstrap";

// style
import { table_translate, table_style } from "../../constants/MaterialTable.js";

// services
import { showMessage, showQuestion } from "../../services/message.js";
import {
  ObterCorreioInterno,
  ListarCategoriasCorreioInterno,
  ListarDestinatariosCorreioInterno,
  PreviaDestinatariosCorreioInterno,
  IncluirCorreioInterno,
  AlterarCorreioInterno,
  EnviarCorreioInterno,
  ObterStatusEnvioCorreioInterno,
  ReenviarFalhasCorreioInterno,
  EnviarArquivoCorreioInterno,
} from "../../services/correio_interno_service.js";

//Context
import { useAuth } from "../../context/auth";

const options_tipoEnvio = [
  { value: "EMAIL", label: "E-mail" },
  { value: "INTERNO", label: "Interno" },
  { value: "AMBOS", label: "Ambos" },
];

// Por padrao o Quill grava alinhamento, fonte e tamanho como classe (ql-align-center,
// ql-size-large...), que so renderiza onde o CSS do Quill esta carregado. O aviso e lido
// no corpo de um e-mail e no portal, onde esse CSS nao existe — entao a formatacao
// precisa sair como estilo inline.
const AlignStyle = Quill.import("attributors/style/align");
const SizeStyle = Quill.import("attributors/style/size");
const FontStyle = Quill.import("attributors/style/font");
Quill.register(AlignStyle, true);
Quill.register(SizeStyle, true);
Quill.register(FontStyle, true);

// Toolbar equivalente a do editor de avisos do Coletas RCC, menos tabela (nao existe no
// Quill 1.x) e edicao de codigo-fonte (permitiria colar HTML arbitrario, que o servidor
// descartaria na sanitizacao). Todo formato abaixo tem correspondencia na whitelist do
// HtmlSanitizerHelper no roc-api — incluir formato aqui sem liberar la faz a formatacao
// sumir depois de salvar.
const quill_toolbar = [
  [{ font: [] }, { size: ["small", false, "large", "huge"] }],
  [{ header: [1, 2, 3, 4, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ script: "sub" }, { script: "super" }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ indent: "-1" }, { indent: "+1" }],
  [{ align: [] }],
  ["blockquote", "code-block"],
  ["link", "image", "anexo"],
  ["clean"],
];

const quill_formats = [
  "font",
  "size",
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "align",
  "blockquote",
  "code-block",
  "link",
  "image",
];

const emailValido = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const TAMANHO_MAXIMO_IMAGEM = 2 * 1024 * 1024;
const TAMANHO_MAXIMO_PDF = 10 * 1024 * 1024;
const TIPOS_IMAGEM_PERMITIDOS = ["image/jpeg", "image/png", "image/gif"];

export default function CadastroCorreioInternoForm({ item, permiteEditar, handleReturn }) {
  const { setLoading } = useAuth();
  const quillRef = React.useRef(null);

  const [correioInterno_Id, set_correioInterno_Id] = useState(0);
  const [titulo, set_titulo] = useState("");
  const [descricao, set_descricao] = useState("");
  const [tipoEnvio, set_tipoEnvio] = useState(options_tipoEnvio[2]);
  const [status, set_status] = useState("RASCUNHO");
  const [categorias, set_categorias] = useState([]);
  const [options_categoria, set_options_categoria] = useState([]);
  const [emails, set_emails] = useState([]);
  const [emailDigitado, set_emailDigitado] = useState("");
  const [destinatarios, set_destinatarios] = useState([]);
  const [statusEnvio, set_statusEnvio] = useState(null);
  const [anexo, set_anexo] = useState(null);

  const permiteAlterar = permiteEditar && status === "RASCUNHO";

  const loadCategorias = () => {
    return ListarCategoriasCorreioInterno()
      .then((result) => {
        const options = (result.data || []).map((c) => ({
          value: c.categoria_Id,
          label: `${c.sigla ? c.sigla + " - " : ""}${c.descricao}`,
        }));
        set_options_categoria(options);
        return options;
      })
      .catch((err) => {
        showMessage("Aviso", err.message, "error", null);
        return [];
      });
  };

  const loadCorreio = (id, options) => {
    return ObterCorreioInterno(id)
      .then((result) => {
        const dados = result.data;

        set_correioInterno_Id(dados.correioInterno_Id);
        set_titulo(dados.titulo);
        set_descricao(dados.descricao || "");
        set_status(dados.status);
        set_tipoEnvio(
          options_tipoEnvio.find((o) => o.value === dados.tipoEnvio) || options_tipoEnvio[2]
        );

        const idsCategorias = (dados.categorias || []).map((c) => c.categoria_Id);
        set_categorias((options || []).filter((o) => idsCategorias.includes(o.value)));

        // Em rascunho os destinatarios gravados sao apenas os e-mails especificos informados.
        // Depois do envio a lista passa a conter todos os cadastros resolvidos por categoria,
        // que nao devem voltar para o campo de e-mails especificos.
        if (dados.status === "RASCUNHO") {
          const emailsEspecificos = (dados.destinatarios || [])
            .filter((d) => d.email)
            .map((d) => d.email);
          set_emails([...new Set(emailsEspecificos)]);
        } else {
          set_emails([]);
        }

        set_destinatarios(dados.destinatarios || []);

        set_anexo(
          dados.arquivoAnexo
            ? { arquivo: dados.arquivoAnexo, nome: dados.nomeAnexo, url: dados.urlAnexo }
            : null
        );

        if (dados.status !== "RASCUNHO") loadStatusEnvio(dados.correioInterno_Id);
      })
      .catch((err) => showMessage("Aviso", err.message, "error", null));
  };

  const loadStatusEnvio = (id) => {
    return ObterStatusEnvioCorreioInterno(id || correioInterno_Id)
      .then((result) => set_statusEnvio(result.data))
      .catch((err) => showMessage("Aviso", err.message, "error", null));
  };

  const loadDestinatarios = () => {
    if (!correioInterno_Id) {
      showMessage("Atenção", "Salve o aviso antes de consultar os destinatários.", "warning", null);
      return;
    }

    setLoading(true);
    ListarDestinatariosCorreioInterno(correioInterno_Id)
      .then((result) => {
        set_destinatarios(result.data || []);
        if (!result.data || result.data.length === 0)
          showMessage(
            "Atenção",
            "Nenhum destinatário encontrado. Os destinatários por categoria são resolvidos no envio.",
            "warning",
            null
          );
      })
      .catch((err) => showMessage("Aviso", err.message, "error", null))
      .finally(() => setLoading(false));
  };

  const handleAdicionarEmail = () => {
    const email = emailDigitado.trim();

    if (!email) return;

    if (!emailValido(email)) {
      showMessage("Atenção", "Informe um e-mail válido.", "warning", null);
      return;
    }

    if (emails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      showMessage("Atenção", "Este e-mail já foi informado.", "warning", null);
      return;
    }

    set_emails([...emails, email]);
    set_emailDigitado("");
  };

  const handleRemoverEmail = (email) => {
    set_emails(emails.filter((e) => e !== email));
  };

  // O botao de imagem do Quill embute o arquivo em base64 dentro do proprio HTML. Aqui ele
  // e substituido por upload: a imagem vai para o servidor e o editor insere apenas a URL,
  // mantendo leve a descricao gravada e o corpo de cada e-mail disparado.
  const handleImagem = React.useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/jpeg,image/png,image/gif");

    // Handler antes do click: o dialogo abre de forma assincrona, mas registrar depois
    // deixa uma janela em que a selecao do arquivo nao seria capturada.
    input.onchange = () => {
      const arquivo = input.files && input.files[0];

      if (!arquivo) return;

      if (!TIPOS_IMAGEM_PERMITIDOS.includes(arquivo.type)) {
        showMessage("Atenção", "Formato inválido. Envie uma imagem JPG, PNG ou GIF.", "warning", null);
        return;
      }

      if (arquivo.size > TAMANHO_MAXIMO_IMAGEM) {
        showMessage("Atenção", "A imagem deve ter no máximo 2 MB.", "warning", null);
        return;
      }

      const formData = new FormData();
      formData.append("arquivo", arquivo);

      setLoading(true);
      EnviarArquivoCorreioInterno(formData)
        .then((result) => {
          const editor = quillRef.current && quillRef.current.getEditor();

          if (!editor) return;

          const selecao = editor.getSelection(true);
          const posicao = selecao ? selecao.index : editor.getLength();

          editor.insertEmbed(posicao, "image", result.data.url);
          editor.setSelection(posicao + 1);
        })
        .catch((err) => showMessage("Aviso", err.message, "error", null))
        .finally(() => setLoading(false));
    };

    input.click();
  }, [setLoading]);

  // PDF entra como link no texto, nao como anexo do e-mail: um anexo viaja junto com cada
  // mensagem enviada, enquanto o link e um arquivo so, servido por URL.
  const handleAnexoPdf = React.useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "application/pdf");

    input.onchange = () => {
      const arquivo = input.files && input.files[0];

      if (!arquivo) return;

      if (arquivo.type !== "application/pdf") {
        showMessage("Atenção", "Envie um arquivo PDF.", "warning", null);
        return;
      }

      if (arquivo.size > TAMANHO_MAXIMO_PDF) {
        showMessage("Atenção", "O arquivo deve ter no máximo 10 MB.", "warning", null);
        return;
      }

      const formData = new FormData();
      formData.append("arquivo", arquivo);

      setLoading(true);
      EnviarArquivoCorreioInterno(formData)
        .then((result) => {
          const editor = quillRef.current && quillRef.current.getEditor();

          if (!editor) return;

          const selecao = editor.getSelection(true);
          const posicao = selecao ? selecao.index : editor.getLength();
          const textoSelecionado = selecao && selecao.length > 0
            ? editor.getText(selecao.index, selecao.length)
            : arquivo.name;

          if (selecao && selecao.length > 0) editor.deleteText(selecao.index, selecao.length);

          editor.insertText(posicao, textoSelecionado, "link", result.data.url);
          editor.setSelection(posicao + textoSelecionado.length);
        })
        .catch((err) => showMessage("Aviso", err.message, "error", null))
        .finally(() => setLoading(false));
    };

    input.click();
  }, [setLoading]);

  const quill_modules = React.useMemo(
    () => ({
      toolbar: {
        container: quill_toolbar,
        handlers: { image: handleImagem, anexo: handleAnexoPdf },
      },
    }),
    [handleImagem, handleAnexoPdf]
  );

  const montaPayload = () => ({
    titulo: titulo.trim(),
    descricao: descricao,
    tipoEnvio: tipoEnvio ? tipoEnvio.value : "",
    categorias: categorias.map((c) => c.value),
    emailsEspecificos: emails,
    arquivoAnexo: anexo ? anexo.arquivo : null,
    nomeAnexo: anexo ? anexo.nome : null,
  });

  // O anexo vai grudado em cada e-mail enviado, diferente do PDF inserido como link no
  // texto, que viaja apenas como URL.
  const handleSelecionarAnexo = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "application/pdf,image/jpeg,image/png,image/gif");

    input.onchange = () => {
      const arquivo = input.files && input.files[0];

      if (!arquivo) return;

      // O limite do servidor é por tipo: imagem 2 MB, PDF 10 MB. Validar só pelo maior aqui
      // deixaria a imagem de 5 MB subir para receber uma recusa confusa da API.
      const ehImagem = TIPOS_IMAGEM_PERMITIDOS.includes(arquivo.type);
      const limite = ehImagem ? TAMANHO_MAXIMO_IMAGEM : TAMANHO_MAXIMO_PDF;

      if (arquivo.size > limite) {
        showMessage(
          "Atenção",
          ehImagem ? "A imagem deve ter no máximo 2 MB." : "O anexo deve ter no máximo 10 MB.",
          "warning",
          null
        );
        return;
      }

      const formData = new FormData();
      formData.append("arquivo", arquivo);

      setLoading(true);
      EnviarArquivoCorreioInterno(formData)
        .then((result) =>
          set_anexo({
            arquivo: result.data.nomeArquivo,
            nome: result.data.nomeOriginal || arquivo.name,
            url: result.data.url,
          })
        )
        .catch((err) => showMessage("Aviso", err.message, "error", null))
        .finally(() => setLoading(false));
    };

    input.click();
  };

  const handleRemoverAnexo = () => set_anexo(null);

  const validaFormulario = () => {
    if (!titulo || titulo.trim().length === 0) {
      showMessage("Atenção", "Informe o título do aviso.", "warning", null);
      return false;
    }

    if (titulo.trim().length > 200) {
      showMessage("Atenção", "O título deve ter no máximo 200 caracteres.", "warning", null);
      return false;
    }

    const descricaoSemTags = (descricao || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    if (descricaoSemTags.length === 0) {
      showMessage("Atenção", "Informe a descrição do aviso.", "warning", null);
      return false;
    }

    if (categorias.length === 0 && emails.length === 0) {
      showMessage(
        "Atenção",
        "Selecione ao menos uma categoria ou informe ao menos um e-mail específico.",
        "warning",
        null
      );
      return false;
    }

    return true;
  };

  const handleSalvar = (callback) => {
    if (!validaFormulario()) return;

    setLoading(true);

    const promise = correioInterno_Id
      ? AlterarCorreioInterno(correioInterno_Id, montaPayload())
      : IncluirCorreioInterno(montaPayload());

    promise
      .then((result) => {
        const dados = result.data;
        set_correioInterno_Id(dados.correioInterno_Id);
        set_status(dados.status);
        set_destinatarios(dados.destinatarios || []);

        // O callback devolve a promise da proxima etapa para o loading so sair ao fim dela.
        if (callback) return callback(dados.correioInterno_Id);
        showMessage("Aviso", "Aviso salvo com sucesso!", "success", null);
      })
      .catch((err) => showMessage("Aviso", err.message, "error", null))
      .finally(() => setLoading(false));
  };

  const handleEnviar = () => {
    if (!validaFormulario()) return;

    // Salva antes para garantir que o envio use o conteudo que esta na tela.
    handleSalvar((id) => {
      // Havendo e-mail especifico, somente ele recebe e as categorias sao ignoradas (regra do
      // CE-3790). A confirmacao deixa isso explicito para o gestor antes do disparo.
      let alcance;

      if (emails.length > 0) {
        const exibidos = emails.slice(0, 5).join(", ");
        const restantes = emails.length > 5 ? ` e mais ${emails.length - 5}` : "";
        alcance = `somente para ${emails.length} e-mail(s) específico(s): ${exibidos}${restantes}`;

        if (categorias.length > 0)
          alcance += ". As categorias selecionadas serão desconsideradas";
      } else {
        alcance = `para todos os cadastros ativos da(s) categoria(s): ${categorias.map((c) => c.label).join(", ")}`;
      }

      // A previa roda a mesma resolucao do envio sem gravar, entao os totais sao os que o
      // disparo vai gerar agora.
      return PreviaDestinatariosCorreioInterno(id)
        .then((result) => {
          const previa = result.data;

          if (!previa.totalDestinatarios) {
            showMessage("Aviso", "Nenhum destinatário encontrado para este aviso.", "warning", null);
            return;
          }

          let totais = `Total de destinatários: ${previa.totalDestinatarios}`;
          if (previa.totalEmails) totais += `, ${previa.totalEmails} por e-mail`;
          // No tipo E-mail, quem nao entra na fila (endereco repetido) nao recebe nada.
          if (previa.totalSomenteInterno)
            totais += tipoEnvio && tipoEnvio.value === "EMAIL"
              ? `, ${previa.totalSomenteInterno} sem disparo por e-mail repetido`
              : `, ${previa.totalSomenteInterno} somente no portal`;

          showQuestion(
            "Atenção",
            `O aviso será enviado ${alcance}. ${totais}. Esta operação não pode ser desfeita. Deseja continuar?`,
            "question",
            function (confirmado) {
              if (!confirmado) return;

              setLoading(true);
              EnviarCorreioInterno(id)
                .then((envio) => {
                  set_status(envio.data.status);
                  showMessage("Aviso", envio.data.mensagem, "success", null);
                  loadStatusEnvio(id);
                })
                .catch((err) => showMessage("Aviso", err.message, "error", null))
                .finally(() => setLoading(false));
            }
          );
        })
        .catch((err) => showMessage("Aviso", err.message, "error", null));
    });
  };

  const handleReenviarFalhas = () => {
    showQuestion(
      "Atenção",
      "Deseja reenviar somente os destinatários com erro?",
      "question",
      function (result) {
        if (!result) return;

        setLoading(true);
        ReenviarFalhasCorreioInterno(correioInterno_Id)
          .then((envio) => {
            set_status(envio.data.status);
            showMessage("Aviso", envio.data.mensagem, "success", null);
            loadStatusEnvio(correioInterno_Id);
          })
          .catch((err) => showMessage("Aviso", err.message, "error", null))
          .finally(() => setLoading(false));
      }
    );
  };

  // Botao proprio na toolbar nasce vazio: o Quill so desenha icone para os formatos dele.
  useEffect(() => {
    const botao = document.querySelector(".correio-interno-form .ql-toolbar button.ql-anexo");

    if (botao && !botao.firstChild) {
      botao.innerHTML = '<i class="fa fa-paperclip"></i>';
      botao.setAttribute("title", "Anexar PDF");
    }
  });

  useEffect(() => {
    setLoading(true);
    loadCategorias()
      .then((options) => {
        if (item && item.correioInterno_Id) return loadCorreio(item.correioInterno_Id, options);
        return null;
      })
      .finally(() => setLoading(false));
  }, []);


  const descricaoEmTexto = (descricao || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

  const corDoStatus = (valor) => {
    if (valor === "ENVIADO") return "success";
    if (valor === "ERRO") return "danger";
    if (valor === "PROCESSANDO") return "info";
    return "secondary";
  };

  const totalDestinatariosPrevisto = destinatarios.length;

  return (
    <div className="content correio-interno-form">
      <style>
        {`
          .correio-interno-form .ql-container,
          .correio-interno-form .ql-editor {
            min-height: 320px;
          }
          .correio-interno-form .ql-container {
            font-size: 14px;
          }
          .correio-interno-form .ql-toolbar,
          .correio-interno-form .ql-container {
            border-color: #dddddd;
          }
          .correio-interno-form .secao-titulo {
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0.4px;
            text-transform: uppercase;
            color: #66615b;
            margin-bottom: 4px;
          }
          .correio-interno-form .secao-ajuda {
            color: #9a9a9a;
            font-size: 12px;
          }
          /* O botao colado no input e o padrao do input-group, mas aqui ele e uma acao
             separada ("adicionar a lista"), nao um complemento do campo. */
          .correio-interno-form .input-group-append .btn {
            margin-left: 10px;
            border-radius: 4px;
          }
          .correio-interno-form .card-footer .btn + .btn {
            margin-left: 10px;
          }
          .correio-interno-form .lista-emails {
            margin-top: 10px;
            padding: 10px 12px 6px 12px;
            background: #fbfbfa;
            border: 1px solid #eeeeee;
            border-radius: 6px;
          }
          .correio-interno-form .lista-emails-cabecalho {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            color: #9a9a9a;
            margin-bottom: 8px;
          }
          .correio-interno-form .chip-email {
            display: inline-flex;
            align-items: center;
            max-width: 100%;
            background: #ffffff;
            border: 1px solid #dcdcdc;
            border-radius: 16px;
            padding: 4px 6px 4px 12px;
            margin: 0 6px 6px 0;
            font-size: 12px;
            line-height: 18px;
            color: #66615b;
          }
          .correio-interno-form .chip-email > i {
            margin-right: 7px;
            color: #51cbce;
            font-size: 11px;
          }
          .correio-interno-form .chip-email-texto {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .correio-interno-form .chip-email-remover {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 18px;
            height: 18px;
            margin-left: 8px;
            padding: 0;
            border: none;
            border-radius: 50%;
            background: transparent;
            color: #9a9a9a;
            font-size: 10px;
            cursor: pointer;
            transition: background 0.2s ease, color 0.2s ease;
          }
          .correio-interno-form .chip-email-remover:hover {
            background: #ef8157;
            color: #ffffff;
          }
          .correio-interno-form .painel-status {
            background: #f9f9f7;
            border: 1px solid #eeeeee;
            border-radius: 6px;
            padding: 14px;
            text-align: center;
          }
          .correio-interno-form .painel-status .numero {
            font-size: 26px;
            font-weight: 600;
            line-height: 1.1;
          }
          .correio-interno-form .painel-status .rotulo {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            color: #9a9a9a;
          }
          .correio-interno-form .contador-titulo {
            font-size: 11px;
            color: #9a9a9a;
          }
        `}
      </style>

      <Row>
        <Col md="12">
          <Button color="Primary" onClick={handleReturn}>
            <i className="fas fa-chevron-left"></i> Voltar
          </Button>

          <Card className="card-user">
            <CardHeader>
              <Row>
                <Col md="8">
                  <h5 className="title mb-0">
                    {correioInterno_Id ? "Editar Aviso" : "Novo Aviso"}{" "}
                    <Badge color={corDoStatus(status)} className="ml-2">
                      {status}
                    </Badge>
                  </h5>
                  {!permiteAlterar && correioInterno_Id > 0 && (
                    <small className="secao-ajuda">
                      Aviso já enviado — edição e exclusão bloqueadas.
                    </small>
                  )}
                </Col>
                <Col md="4" className="text-right">
                  <label className="text-danger">* Campos Obrigatórios</label>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <Form>
                <div className="secao-titulo">Identificação</div>
                <hr className="mt-1" />

                <Row>
                  <Col md="8">
                    <FormGroup>
                      <Label>Título *</Label>
                      <Input
                        type="text"
                        maxLength={200}
                        value={titulo}
                        disabled={!permiteAlterar}
                        onChange={(e) => set_titulo(e.target.value)}
                        placeholder="Assunto que o cadastro verá no e-mail e no portal"
                      />
                      <span className="contador-titulo">{titulo.length}/200 caracteres</span>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Tipo de envio *</Label>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        value={tipoEnvio}
                        onChange={set_tipoEnvio}
                        options={options_tipoEnvio}
                        isDisabled={!permiteAlterar}
                      />
                      <span className="secao-ajuda">
                        {tipoEnvio && tipoEnvio.value === "EMAIL" &&
                          "Somente e-mail — o aviso não aparece no portal."}
                        {tipoEnvio && tipoEnvio.value === "INTERNO" &&
                          "Somente no portal — nenhum e-mail é disparado."}
                        {tipoEnvio && tipoEnvio.value === "AMBOS" &&
                          "E-mail e aviso no portal."}
                      </span>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="secao-titulo mt-4">Destinatários</div>
                <hr className="mt-1" />
                <p className="secao-ajuda">
                  Informe categorias, e-mails específicos, ou os dois. Os cadastros das categorias
                  são resolvidos no momento do envio.
                </p>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Categorias / Modalidades</Label>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        isMulti
                        closeMenuOnSelect={false}
                        value={categorias}
                        onChange={(value) => set_categorias(value || [])}
                        options={options_categoria}
                        isDisabled={!permiteAlterar}
                        placeholder="Selecione uma ou mais categorias"
                        noOptionsMessage={() => "Nenhuma categoria disponível"}
                      />
                      <span className="secao-ajuda">
                        {categorias.length === 0
                          ? "Nenhuma categoria selecionada."
                          : `${categorias.length} categoria(s) selecionada(s).`}
                      </span>
                    </FormGroup>
                  </Col>

                  <Col md="6">
                    <FormGroup>
                      <Label>E-mails específicos</Label>
                      <InputGroup>
                        <Input
                          type="email"
                          value={emailDigitado}
                          disabled={!permiteAlterar}
                          onChange={(e) => set_emailDigitado(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAdicionarEmail();
                            }
                          }}
                          placeholder="email@dominio.com.br"
                        />
                        <InputGroupAddon addonType="append">
                          <Button
                            color="success"
                            disabled={!permiteAlterar || !emailDigitado}
                            onClick={handleAdicionarEmail}
                          >
                            <i className="fa fa-plus"></i> Adicionar
                          </Button>
                        </InputGroupAddon>
                      </InputGroup>
                      <span className="secao-ajuda">
                        O e-mail precisa estar cadastrado em algum protocolo — a API recusa o
                        envio caso contrário.
                      </span>

                      {emails.length > 0 && (
                        <div className="lista-emails">
                          <div className="lista-emails-cabecalho">
                            {emails.length} e-mail(s) adicionado(s)
                          </div>
                          {emails.map((email) => (
                            <span className="chip-email" key={email}>
                              <i className="fa fa-envelope"></i>
                              <span className="chip-email-texto">{email}</span>
                              {permiteAlterar && (
                                <button
                                  type="button"
                                  className="chip-email-remover"
                                  title="Remover"
                                  aria-label={"Remover " + email}
                                  onClick={() => handleRemoverEmail(email)}
                                >
                                  <i className="fa fa-times"></i>
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <div className="secao-titulo mt-4">Conteúdo do aviso</div>
                <hr className="mt-1" />

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label>Descrição *</Label>
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={descricao}
                        onChange={set_descricao}
                        modules={quill_modules}
                        formats={quill_formats}
                        readOnly={!permiteAlterar}
                      />
                      {descricaoEmTexto.length === 0 && permiteAlterar && (
                        <span className="secao-ajuda">
                          Formatação, listas, cores, links, imagens (JPG, PNG ou GIF, até 2 MB) e anexo de PDF (até 10 MB, inserido como link).
                        </span>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label>Anexo do e-mail</Label>

                      {!anexo && (
                        <div>
                          <Button color="info" disabled={!permiteAlterar} onClick={handleSelecionarAnexo}>
                            <i className="fa fa-paperclip"></i> Selecionar anexo
                          </Button>
                        </div>
                      )}

                      {anexo && (
                        <div className="lista-emails">
                          <span className="chip-email">
                            <i className="fa fa-file"></i>
                            <span className="chip-email-texto">
                              {anexo.url ? (
                                <a href={anexo.url} target="_blank" rel="noopener noreferrer">
                                  {anexo.nome}
                                </a>
                              ) : (
                                anexo.nome
                              )}
                            </span>
                            {permiteAlterar && (
                              <button
                                type="button"
                                className="chip-email-remover"
                                title="Remover anexo"
                                aria-label="Remover anexo"
                                onClick={handleRemoverAnexo}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            )}
                          </span>
                        </div>
                      )}

                      <span className="secao-ajuda">
                        O arquivo vai anexado em <strong>cada</strong> e-mail enviado. Para muitos
                        destinatários, prefira inserir o PDF como link no texto — o e-mail fica leve
                        e o provedor não recusa o envio. Máximo de 10 MB e um anexo por aviso.
                      </span>
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </CardBody>

            <CardFooter>
              <Row>
                <Col md="6">
                  {correioInterno_Id > 0 && (
                    <Button color="info" className="btn-mobile" onClick={loadDestinatarios}>
                      <i className="fa fa-users"></i> Prévia dos destinatários
                      {totalDestinatariosPrevisto > 0 && " (" + totalDestinatariosPrevisto + ")"}
                    </Button>
                  )}
                </Col>
                <Col md="6" className="text-right">
                  <Button color="secondary" className="btn-mobile" onClick={handleReturn}>
                    Cancelar
                  </Button>
                  {permiteAlterar && (
                    <Button color="success" className="btn-mobile" onClick={() => handleSalvar(null)}>
                      <i className="fa fa-save"></i> Salvar rascunho
                    </Button>
                  )}
                  {permiteAlterar && (
                    <Button color="warning" className="btn-mobile" onClick={handleEnviar}>
                      <i className="fa fa-paper-plane"></i> Enviar
                    </Button>
                  )}
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>

      {correioInterno_Id > 0 && status !== "RASCUNHO" && (
        <Row>
          <Col md="12">
            <Card className="card-user">
              <CardHeader>
                <h5 className="title mb-0">Acompanhamento do envio</h5>
                <small className="secao-ajuda">
                  O envio roda em segundo plano — a tela não aguarda a conclusão.
                </small>
              </CardHeader>
              <CardBody>
                {statusEnvio && (
                  <Row>
                    <Col md="3" xs="6" className="mb-2">
                      <div className="painel-status">
                        <div className="numero">{statusEnvio.totalDestinatarios}</div>
                        <div className="rotulo">Destinatários</div>
                      </div>
                    </Col>
                    <Col md="3" xs="6" className="mb-2">
                      <div className="painel-status">
                        <div className="numero text-info">{statusEnvio.pendentes}</div>
                        <div className="rotulo">Pendentes</div>
                      </div>
                    </Col>
                    <Col md="3" xs="6" className="mb-2">
                      <div className="painel-status">
                        <div className="numero text-success">{statusEnvio.enviados}</div>
                        <div className="rotulo">E-mails enviados</div>
                      </div>
                    </Col>
                    <Col md="3" xs="6" className="mb-2">
                      <div className="painel-status">
                        <div className="numero text-danger">{statusEnvio.comErro}</div>
                        <div className="rotulo">Com erro</div>
                      </div>
                    </Col>
                  </Row>
                )}

                {statusEnvio && statusEnvio.somenteInterno > 0 && (
                  <p className="secao-ajuda mt-2">
                    {statusEnvio.somenteInterno} cadastro(s) recebem apenas a mensagem interna —
                    sem e-mail cadastrado, e-mail já atendido por outro cadastro, ou aviso do tipo
                    Interno.
                  </p>
                )}
              </CardBody>
              <CardFooter>
                <Button
                  color="secondary"
                  className="btn-mobile"
                  onClick={() => loadStatusEnvio(correioInterno_Id)}
                >
                  <i className="fa fa-sync"></i> Atualizar status
                </Button>
                {statusEnvio && statusEnvio.comErro > 0 && (
                  <Button color="warning" className="btn-mobile" onClick={handleReenviarFalhas}>
                    <i className="fa fa-redo"></i> Reenviar falhas
                  </Button>
                )}
              </CardFooter>
            </Card>
          </Col>
        </Row>
      )}

      {destinatarios.length > 0 && (
        <Row>
          <Col md="12">
            <MaterialTable
              style={{ minHeight: "auto" }}
              title="Destinatários"
              columns={[
                { title: "Protocolo", field: "numeroProtocolo" },
                { title: "Nome", field: "nomeSolicitante" },
                { title: "E-mail", field: "email" },
                {
                  title: "Status",
                  field: "statusEnvio",
                  render: (rowData) => (
                    <Badge color={corDoStatus(rowData.statusEnvio)}>
                      {rowData.statusEnvio === "INTERNO" ? "SOMENTE INTERNO" : rowData.statusEnvio}
                    </Badge>
                  ),
                },
                { title: "Tentativas", field: "tentativasEnvio" },
                { title: "Erro", field: "mensagemErro" },
              ]}
              data={destinatarios}
              options={{ ...table_style, filtering: false, exportButton: true }}
              localization={table_translate}
            />
          </Col>
        </Row>
      )}
    </div>
  );
}
